import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GetFormula, UpdateFormula } from "../../../Service/_index";
import { FORMULA_NAMES } from "../../../Service/SearchPolicy";
import { ToastSuccess, ToastError } from "../../../UiComponents/Toaster/Toast";
import {
  deriveIdvRange,
  midpointIdv,
  describeIdvSide,
  describeDepreciation,
  setActiveIdvFormula,
  getActiveIdvFormula,
} from "../../Policy/OnlinePolicy/utils/idvHelpers";

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || v === "" || v === null) return "—";
  return n.toLocaleString("en-IN");
};

/** One result figure, with the rule that produced it underneath. */
const ResultCard = ({ label, value, formula, highlight }) => (
  <Box
    sx={{
      p: 1.5,
      height: "100%",
      borderRadius: "8px",
      backgroundColor: "#F8F9FA",
      border: highlight ? "2px solid #1976d2" : "1px solid #EAECF0",
    }}
  >
    <Typography sx={{ fontSize: 12, color: "#667085", mb: 0.5 }}>{label}</Typography>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 18,
        color: highlight ? "#1976d2" : "#101828",
      }}
    >
      ₹ {money(value)}
    </Typography>
    <Typography sx={{ fontSize: 11, color: "#98A2B3", mt: 0.5 }}>{formula}</Typography>
  </Box>
);

// A text field that may shrink inside a flex row. MUI inputs carry a default
// min-width wide enough to push "Price ×" onto its own line in a half-width
// card, so the min-width is dropped and the field is allowed to flex instead.
const compactField = (flex) => ({
  flex,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    "&.Mui-disabled": { backgroundColor: "#F2F4F7" },
  },
  // index.scss:618 forces `border-color: transparent !important` on EVERY
  // outlined input in the app, focus included — which is why these rendered as
  // plain text with nothing to show they could be typed in. Beating that needs
  // both a higher-specificity selector (three classes to its two) and
  // !important; styling `fieldset` alone silently loses to it.
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D0D5DD !important",
    borderWidth: "1px !important",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2 !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2 !important",
    borderWidth: "2px !important",
  },
  // Read-only users get a visibly inert control rather than one that merely
  // ignores them.
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#EAECF0 !important",
  },
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    fontWeight: 600,
    color: "#101828",
    textAlign: "center",
    padding: "8px 4px",
    cursor: "text",
  },
  // Adornments sit tight against the value so the whole thing reads as one
  // expression rather than three separate controls.
  "& .MuiInputAdornment-root .MuiTypography-root": {
    fontSize: 13,
    color: "#667085",
  },
  "& .MuiInputAdornment-positionStart": { marginRight: 2 },
  "& .MuiInputAdornment-positionEnd": { marginLeft: 2 },
});

const operatorStyle = {
  fontSize: 13,
  color: "#667085",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

/** One percentage of the formula, read as a single line: "IDV × [95] %". */
const SideEditor = ({ title, lead, percent, onPercent, disabled }) => (
  <Box
    sx={{
      p: 1.5,
      height: "100%",
      borderRadius: "8px",
      border: "1px solid #EAECF0",
      backgroundColor: "#FCFCFD",
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 1.25 }}>
      {title}
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "nowrap",
      }}
    >
      <Typography sx={operatorStyle}>{lead}</Typography>
      <TextField
        size="small"
        value={percent}
        disabled={disabled}
        onChange={(e) => onPercent(e.target.value.replace(/[^0-9.]/g, ""))}
        sx={compactField(1)}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
      />
    </Box>
  </Box>
);

/**
 * Ex-showroom price -> IDV range, and the editor for the rule itself.
 *
 * The percentages are a single saved record shared by the whole system, so what
 * is edited here changes how every Make & Model range is filled from then on.
 * Only a CLIENT (the master admin) may change it — everyone else sees the same
 * figures read-only, because this one record decides the IDV of every policy
 * quoted afterwards.
 *
 * Opened from a row it also compares the CALCULATED range with the range
 * actually STORED on that record: the master lets either figure be edited after
 * the calculator fills it, and the stored pair is what a policy really uses.
 */
const IdvCalculatorDialog = ({ open, onClose, row, canEdit }) => {
  const [price, setPrice] = useState("");
  const [form, setForm] = useState(() => getActiveIdvFormula());
  const [saved, setSaved] = useState(() => getActiveIdvFormula());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrice(
      row?.manufacturerSellingPrice
        ? String(row.manufacturerSellingPrice).replace(/[^0-9.]/g, "")
        : ""
    );

    let cancelled = false;
    setLoading(true);
    GetFormula(FORMULA_NAMES.IDV_RANGE)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.values) {
          const next = setActiveIdvFormula(res.data.values);
          setForm(next);
          setSaved(next);
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [open, row]);

  // Preview against what is on screen, not what is saved — the point of the
  // editor is seeing the effect before committing to it.
  const { from, to, idv } = deriveIdvRange(price, form);
  const mid = midpointIdv(from, to);

  const dirty =
    Number(form.depreciationPercent) !== Number(saved.depreciationPercent) ||
    Number(form.minPercent) !== Number(saved.minPercent) ||
    Number(form.maxPercent) !== Number(saved.maxPercent);

  const storedFrom =
    row?.idvRangeFrom === undefined || row?.idvRangeFrom === null
      ? ""
      : String(row.idvRangeFrom).trim();
  const storedTo =
    row?.idvRangeTo === undefined || row?.idvRangeTo === null
      ? ""
      : String(row.idvRangeTo).trim();
  const hasStored = !!(storedFrom || storedTo);
  // Only claim a verdict when there is something to compare against. With no
  // price there is no calculated range, and calling that "matches the formula"
  // would vouch for a range nothing has checked.
  const comparable = hasStored && !!from;
  const differs = comparable && (storedFrom !== from || storedTo !== to);

  const handleSave = async () => {
    const dep = Number(form.depreciationPercent);
    const min = Number(form.minPercent);
    const max = Number(form.maxPercent);

    if (!Number.isFinite(dep) || dep < 0 || dep >= 100) {
      ToastError("Depreciation must be between 0 and 99");
      return;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) {
      ToastError("Min and Max must be numbers greater than 0");
      return;
    }
    if (min > max) {
      ToastError("Min cannot be higher than Max");
      return;
    }

    setSaving(true);
    const res = await UpdateFormula(FORMULA_NAMES.IDV_RANGE, {
      values: {
        depreciationPercent: dep,
        minPercent: min,
        maxPercent: max,
      },
      updatedBy: sessionStorage.getItem("UserId") || null,
    });
    setSaving(false);

    if (res && !res.error) {
      const next = setActiveIdvFormula(res.data?.values || form);
      setForm(next);
      setSaved(next);
      ToastSuccess("IDV formula saved");
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 20,
          pb: 2,
          borderBottom: "1px solid #EAECF0",
          color: "#1976d2",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          IDV Calculator
          <Typography sx={{ fontSize: 13, color: "#667085", fontWeight: 400 }}>
            {row?.make ? `${row.make} ${row.model}` : "Ex-showroom price to IDV range"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Typography sx={{ fontSize: 12, color: "#344054", fontWeight: 600, mb: 1 }}>
          Ex-Showroom / Manufacturer's Selling Price
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={price}
          autoFocus
          placeholder="Type any amount to calculate"
          onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
          sx={{
            "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
            // Same global `border-color: transparent !important` applies here.
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D0D5DD !important",
              borderWidth: "1px !important",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#1976d2 !important",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: "#1976d2 !important",
                borderWidth: "2px !important",
              },
            "& .MuiOutlinedInput-input": { fontSize: 14, fontWeight: 600 },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
          }}
        />

        <Grid container spacing={1.5} sx={{ mt: 0.5 }} alignItems="stretch">
          <Grid item xs={12} sm={3}>
            <ResultCard
              label="IDV"
              value={idv}
              formula={describeDepreciation(form.depreciationPercent)}
              highlight
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <ResultCard
              label="Min (IDV Range From)"
              value={from}
              formula={describeIdvSide(form.minPercent)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <ResultCard
              label="Max (IDV Range To)"
              value={to}
              formula={describeIdvSide(form.maxPercent)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <ResultCard
              label="IDV used on a policy"
              value={mid}
              formula="Midpoint of the range"
            />
          </Grid>
        </Grid>

        {hasStored && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${differs ? "#FEC84B" : "#EAECF0"}`,
              backgroundColor: differs ? "#FFFCF5" : "#F8F9FA",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054" }}>
                Range saved on this record
              </Typography>
              <Chip
                size="small"
                label={
                  !comparable
                    ? "No price to compare"
                    : differs
                      ? "Edited by hand"
                      : "Matches the formula"
                }
                sx={{
                  height: 18,
                  fontSize: 10,
                  backgroundColor: !comparable
                    ? "#F2F4F7"
                    : differs
                      ? "#FEF0C7"
                      : "#ECFDF3",
                  color: !comparable ? "#667085" : differs ? "#B54708" : "#027A48",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, color: "#101828" }}>
              ₹ {money(storedFrom)} — ₹ {money(storedTo)}
            </Typography>
            {differs && (
              <Typography sx={{ fontSize: 11, color: "#B54708", mt: 0.5 }}>
                This is what the Online Policy will actually use. The saved range
                wins over the formula, so a policy for this vehicle is quoted on
                the figures above, not on the calculated ones.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#344054" }}>
            The formula
          </Typography>
          {loading && <CircularProgress size={12} />}
          {/* Which boxes accept typing was not obvious from the styling alone,
              so the state is stated outright rather than implied. */}
          <Chip
            size="small"
            label={canEdit ? "Editable — type in the boxes" : "View only"}
            sx={{
              height: 18,
              fontSize: 10,
              backgroundColor: canEdit ? "#ECFDF3" : "#F2F4F7",
              color: canEdit ? "#027A48" : "#667085",
            }}
          />
        </Box>

        <Grid container spacing={1.5} alignItems="stretch">
          <Grid item xs={12} sm={4}>
            <SideEditor
              title="Depreciation"
              lead="Price −"
              percent={form.depreciationPercent}
              disabled={!canEdit || saving}
              onPercent={(v) => setForm((f) => ({ ...f, depreciationPercent: v }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SideEditor
              title="Min (IDV Range From)"
              lead="IDV ×"
              percent={form.minPercent}
              disabled={!canEdit || saving}
              onPercent={(v) => setForm((f) => ({ ...f, minPercent: v }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SideEditor
              title="Max (IDV Range To)"
              lead="IDV ×"
              percent={form.maxPercent}
              disabled={!canEdit || saving}
              onPercent={(v) => setForm((f) => ({ ...f, maxPercent: v }))}
            />
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: 11, color: "#667085", mt: 1.5 }}>
          Saved once and used everywhere: this rule fills the IDV range on every
          Make &amp; Model record from its selling price. Both range figures stay
          editable on the record afterwards, and a range typed by hand{" "}
          <b>overrides this formula</b>. A policy pre-fills the midpoint of
          whichever range applies.
        </Typography>

        {canEdit && dirty && (
          <Typography sx={{ fontSize: 11, color: "#B54708", mt: 1 }}>
            Unsaved changes — the figures above are a preview until you save.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #EAECF0" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={!dirty || saving || loading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 1,
              px: 2.5,
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            {saving ? "Saving..." : "Save Formula"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IdvCalculatorDialog;
